// src/components/onboarding/OnboardingBar.tsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OnboardingStepId, useOnboarding } from "../../context/OnboardingContext";

// StepId includes route-based steps + all OnboardingStepId values
type StepId = "goToSources" | OnboardingStepId;

const STEPS: { id: StepId; label: string }[] = [
    { id: "goToSources", label: "Go to Sources" },
    { id: "project", label: "Select project" },
    { id: "baseline", label: "Upload baseline" },
    { id: "documents", label: "Add docs" },
    { id: "analysis", label: "Run analysis" },
    { id: "reviewFlag", label: "Review flags" },
    { id: "export", label: "Draft ASR" },
];

const OnboardingBar: React.FC = () => {
    const navigate = useNavigate();

    // Use the REAL context API: state + onboarding progress
    const { state } = useOnboarding();
    const pathname = location.pathname.toLowerCase();

    const isStepComplete = (id: StepId): boolean => {
        // Step 1: Go to Sources – treat it as complete when the user is
        // on Sources or Workbench, OR when the context says it's done.
        if (id === "goToSources") {
            if (pathname.startsWith("/sources") || pathname.startsWith("/workbench")) {
                return true;
            }
        }

        // All other steps (and persisted Go to Sources) come from onboarding state
        const key = id as OnboardingStepId;
        return Boolean(state.completed[key]);
    };


    const isStepCompleteLocal = (id: StepId): boolean => {
        // Just delegate to the context helper; it's already normalized + persisted
        return isStepComplete(id as OnboardingStepId);
    };


    const { stepsWithStatus, allComplete, currentIndex, progress } = useMemo(() => {
        const list = STEPS.map((step) => {
            const complete = isStepCompleteLocal(step.id);
            return { ...step, complete };
        });


        const firstIncompleteIndex = list.findIndex((s) => !s.complete);
        const allDone = list.every((s) => s.complete);
        const completedCount = list.filter((s) => s.complete).length;
        const pct =
            list.length === 0 ? 0 : (completedCount / list.length) * 100;

        return {
            stepsWithStatus: list,
            allComplete: allDone,
            currentIndex: allDone ? list.length - 1 : firstIncompleteIndex,
            progress: pct,
        };
        // Recompute when route changes or onboarding state changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, state]);

    if (allComplete) {
        return null;
    }

    const handleClick = (id: StepId) => {
        switch (id) {
            case "goToSources":
            case "project":
            case "baseline":
            case "documents":
                navigate("/sources");
                break;
            case "analysis":
            case "reviewFlag":
            case "export":
                navigate("/workbench");
                break;
            default:
                break;
        }
    };

    return (
        <div className="border-b border-amber-500/20 bg-slate-950/95 px-4 py-3 text-slate-100 shadow-sm backdrop-blur">
            <div className="mx-auto flex max-w-6xl flex-col gap-3">
                {/* Row 1 – Centered title */}
                <div className="flex w-full items-center justify-center">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.20em] text-amber-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                        <span>Shield Onboarding</span>
                    </div>
                </div>

                {/* Row 2 – Progress + pills */}
                <div className="flex flex-col gap-2">
                    {/* Progress line */}
                    <div className="relative h-1 overflow-hidden rounded-full bg-slate-800/80">
                        <div
                            className="h-full rounded-full bg-amber-500 transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>

                    {/* Pills */}
                    <div className="flex flex-wrap justify-center gap-2">
                        {stepsWithStatus.map((step, index) => {
                            const isCompleteStep = step.complete;
                            const isCurrent = index === currentIndex;

                            const baseClasses =
                                "inline-flex items-center px-3.5 py-1.5 text-[11px] rounded-full border transition-colors duration-150";

                            const stateClasses = isCompleteStep
                                ? "border-amber-500/60 bg-amber-500/10 text-amber-100"
                                : isCurrent
                                    ? "border-amber-400 bg-slate-900 text-amber-50"
                                    : "border-slate-700 bg-slate-900/70 text-slate-400 hover:border-slate-500 hover:text-slate-100";

                            return (
                                <button
                                    key={step.id}
                                    type="button"
                                    onClick={() => handleClick(step.id)}
                                    className={`${baseClasses} ${stateClasses}`}
                                >
                                    {isCompleteStep
                                        ? `✓ ${step.label}`
                                        : step.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingBar;
